import { watch } from 'vue'
import { ApiServiceMain } from 'boot/api.js'
import { nativeBridge } from 'common/native-bridge.js'
import { useUserAuthStore } from 'src/stores/user/auth'
import { AUTOLOGIN_BRIDGE_MESSAGE, AUTOLOGIN_ERROR_CODES, AUTOLOGIN_EVENTS } from 'src/common/autologin/constants'
import {
  clearAutologinFailure,
  consumeAutologinFailure,
  isTokenProcessed,
  markAutologinFailure,
  markTokenProcessed,
} from 'src/common/autologin/state'
import { consoleLogAPI } from 'src/common/functions'
import { tokenService } from 'src/common/token-service'
import { setStoredLocaleFromProfile } from 'src/common/utils/locale'

function resolveErrorCode(error) {
  const backendCode = error?.response?.data?.error_code

  if (backendCode) {
    return backendCode
  }

  if (!error?.response) {
    return AUTOLOGIN_ERROR_CODES.NETWORK
  }

  return error.response.status >= 500 ? AUTOLOGIN_ERROR_CODES.SERVER : AUTOLOGIN_ERROR_CODES.UNKNOWN
}

export function useAutologin({ dwh } = {}) {
  const userAuthStore = useUserAuthStore()
  let isValidating = false

  async function autologin(payload) {
    const token = payload?.token

    if (!token) {
      dwh?.(AUTOLOGIN_EVENTS.FAILED, { error_code: AUTOLOGIN_ERROR_CODES.UNKNOWN })
      return
    }

    if (isValidating || isTokenProcessed(token) || userAuthStore.getAuth || tokenService.getToken()) {
      return
    }

    isValidating = true

    try {
      const { data } = await ApiServiceMain.autologin.validate(token)

      if (!data?.token) {
        throw new Error('Autologin validate response does not contain token')
      }

      markTokenProcessed(token)
      clearAutologinFailure()
      tokenService.setToken(data.token)
      localStorage.setItem('hasAccount', 'true')

      if (data.user?.language) {
        setStoredLocaleFromProfile(data.user.language)
      }

      userAuthStore.updateToken(data.token)

      dwh?.(AUTOLOGIN_EVENTS.SUCCESS)
      location.reload()
    } catch (e) {
      consoleLogAPI(e)

      if (e?.response) {
        markTokenProcessed(token)
      }

      markAutologinFailure()
      dwh?.(AUTOLOGIN_EVENTS.FAILED, { error_code: resolveErrorCode(e) })
    } finally {
      isValidating = false
    }
  }

  watch(
    () => userAuthStore.getAuth,
    (isAuth) => {
      if (isAuth && consumeAutologinFailure()) {
        dwh?.(AUTOLOGIN_EVENTS.MANUAL_AFTER_FAIL)
      }
    },
    { immediate: true },
  )

  nativeBridge.on(AUTOLOGIN_BRIDGE_MESSAGE, autologin)
}
